<template>
  <EditorPanel>
    <div class="editor-panel__row editor-panel__row--multi">
      <span class="editor-panel__row-label">{{ $t("harmony.upload") }}</span>
      <EditorIconButton v-if="hasImages(siteSetting.galleries[`card${cardSelect}`])" icon="mdi-plus"
        :aria-label="$t('harmony.add-images-gallery')" @click="addNewImagesGallery" />
      <div v-if="hasSchedule(siteSetting.schedules[`card${cardSelect}`])">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <EditorIconButton icon="mdi-list-box" :icon-size="28" :aria-label="$t('harmony.show-schedules')"
              :text="true" v-bind="attrs" v-on="on" @click="showSchedules = true" />
          </template>
          <span>{{ $t('harmony.show-schedules') }}</span>
        </v-tooltip>
      </div>
      <v-select v-model="image_or_video" :items="itemsForUpload" item-text="name" item-value="id" outlined
        hide-details="auto" dense color="#9BA9BD" :disabled="isDisabled"
        class="editor-select--compact rounded-lg m-0 p-0 flex-grow-1" />
      <EditorIconButton v-if="siteSetting.backgrounds[`card${cardSelect}`].path" icon="mdi-trash-can-outline"
        :aria-label="$t('harmony.remove-background')"
        @click="removeBackground(siteSetting.backgrounds[`card${cardSelect}`].path)" />
      <EditorIconButton v-if="siteSetting.videos[`card${cardSelect}`].path" icon="mdi-trash-can-outline"
        :aria-label="$t('harmony.remove-video')" @click="removeVideo(siteSetting.videos[`card${cardSelect}`].path)" />
      <EditorIconButton v-if="hasImages(siteSetting.galleries[`card${cardSelect}`])" icon="mdi-trash-can-outline"
        :aria-label="$t('harmony.remove-gallery')"
        @click="removeGallery(siteSetting.galleries[`card${cardSelect}`].id)" />
      <EditorIconButton
        v-if="!siteSetting.backgrounds[`card${cardSelect}`].path && hasSchedule(siteSetting.schedules[`card${cardSelect}`]) || !siteSetting.backgrounds[`card${cardSelect}`].path && siteSetting.schedules[`card${cardSelect}`].background"
        icon="mdi-trash-can-outline" :aria-label="$t('harmony.remove-schedules')"
        @click="removeSchedules([`card${cardSelect}`])" />
    </div>

    <div class="mt-3">
      <ImageUpload v-if="image_or_video === 1" uploadEndpoint="/upload-background" :selected="'card'"
        :cardSelected="'card' + cardSelect" :siteId="harmony.id"
        :previewUrl="siteSetting.backgrounds[`card${cardSelect}`].path" @image-uploaded="handleImageUploaded" />
    </div>

    <div class="mt-3">
      <VideoUpload v-if="image_or_video === 2" uploadEndpoint="/upload-video" :selected="'card'"
        :cardSelected="'card' + cardSelect" :siteId="harmony.id"
        :previewUrl="siteSetting.videos[`card${cardSelect}`].path" @video-uploaded="handleVideoUploaded" />
    </div>

    <div class="mt-3">
      <ImageUploadGallery v-if="image_or_video === 3" ref="imageUploadGallery" uploadEndpoint="/upload-gallery"
        :selected="'card'" :cardSelected="'card' + cardSelect" :siteId="harmony.id" :updatedGallery="galleryUpdated"
        :galleryId="siteSetting.galleries?.[`card${cardSelect}`]?.id || 0" @images-uploaded="galleryUpdated = false"
        :previewUrl="siteSetting.galleries?.[`card${cardSelect}`]?.images?.[0]?.path || ''"
        @image-uploaded-gallery="handleImageUploaded" />
    </div>

    <div v-if="image_or_video === 4">
      <v-row no-gutters align="start" justify="start" class="my-3">
        <v-btn @click="selectBackground = 2" outlined elevation="0" color="#9BA9BD" class="rounded-lg m-0 p-3 mr-2"
          small v-if="!siteSetting.backgrounds[`card${cardSelect}`].path">
          {{ $t("harmony.add-image") }}
        </v-btn>
        <v-btn @click="selectBackground = 1" outlined elevation="0" color="#9BA9BD" class="rounded-lg p-3 mr-2" small
          v-if="!siteSetting.backgrounds[`card${cardSelect}`].path">
          {{ $t("harmony.add-color") }}
        </v-btn>
        <div class="d-flex justify-space-between mb-auto mt-auto">
          <p v-if="!siteSetting.backgrounds[`card${cardSelect}`].path && !siteSetting.schedules[`card${cardSelect}`].background"
            class="text-left menu-text mb-auto mt-auto">
            {{ $t("harmony.add-image-or-color") }}</p>
        </div>
      </v-row>

      <ImageUpload v-if="selectBackground == 2 || siteSetting.backgrounds[`card${cardSelect}`].path"
        uploadEndpoint="/upload-background" :selected="'card'" :cardSelected="'card' + cardSelect + '/4'"
        :siteId="harmony.id" :previewUrl="siteSetting.backgrounds[`card${cardSelect}`].path"
        @image-uploaded="handleImageUploaded" />
      <v-container class="mt-0 mb-4 colors"
        v-if="selectBackground == 1 && !siteSetting.backgrounds[`card${cardSelect}`].path">
        <v-row class="justify-center">
          <div v-for="color in colors" :key="color.hex" class="pa-0 py-1 d-flex justify-center">
            <v-btn :style="{ backgroundColor: color.hex }" :disabled="loadingSave" class="panelColors"
              @click="selectColor(color.hex)">
            </v-btn>
          </div>
        </v-row>
      </v-container>
    </div>

    <div class="mt-3">
      <ScheduleUpload ref="UploadSchedule" v-if="image_or_video == 4" uploadEndpoint="/upload-schedule"
        :cardSelected="'card' + cardSelect" :siteId="harmony.id"
        :scheduleId="siteSetting.schedules[`card${cardSelect}`].id"
        :schedule="siteSetting.schedules[`card${cardSelect}`].schedule" :showSchedule="showSchedules"
        @close-dialog="showSchedules = false" @show-edit="showEdit = true" @show-addSchedule="showBtnSchedule"
        @updated-schedule="handleImageUploaded" @updated-settings="saveSettings" />
    </div>

    <div class="d-flex justify-space-between align-center mt-2" v-if="image_or_video != 3">
      <EditorSectionLabel class="ma-0">
        {{ $t("harmony.add-link") }} <br><span style="color: #8e84c0">
          {{ getPlatformName(siteSetting.platforms[`card${cardSelect}`].name) }}</span>
      </EditorSectionLabel>

      <div class="d-flex align-center" style="gap: 8px;">
        <v-tooltip bottom
          v-if="getPlatformName(siteSetting.platforms[`card${cardSelect}`].name) && image_or_video != 4">
          <template v-slot:activator="{ on, attrs }">
            <EditorIconButton icon="mdi-pencil" :aria-label="$t('harmony.edit-platform-text')" v-bind="attrs" v-on="on"
              @click="dialogText = true" />
          </template>
          <span>{{ $t('harmony.textTooltip') }}</span>
        </v-tooltip>

        <v-dialog v-model="dialogText" max-width="450px" persistent>
          <v-card class="px-2 py-2 rounded-xl">
            <v-btn icon small @click="dialogText = false" class="close-button" :disabled="loadingSave"
              :aria-label="$t('harmony.close')">
              <v-icon aria-hidden="true">mdi-close</v-icon>
            </v-btn>
            <v-card-title class="text-h7 menu-text px-3">{{ $t("harmony.harmony-text") }}</v-card-title>
            <v-card-subtitle class="text-h7 menu-text px-3 text-left pt-1">{{ $t('harmony.harmony-subtitle') }}
            </v-card-subtitle>

            <v-card-text class="pb-0 px-4">
              <v-text-field ref="platformText" v-model="tempText" label="Escribe el texto" color="#9BA9BD" class="mt-2"
                outlined dense :hint="$t('harmony.logoVisible')" persistent-hint :rules="[rules.required]" />
              <v-select v-model="font" :items="fontsOptions" color="#9BA9BD" label="Selecciona el tipo de fuente"
                class="mt-2 list-fonts-input" outlined dense item-value="value" :item-text="formatText">
                <template v-slot:item="data">
                  <div :class="data.item.value" class="list-fonts" style="font-size: 24px;">
                    {{ data.item.fontName }} : {{ data.item.text }}
                  </div>
                </template>
              </v-select>

              <div class="d-flex">
                <v-select v-model="color" :items="colorOptions" color="#9BA9BD" label="Color"
                  class="mt-2 list-weight-input mr-2" outlined dense item-value="value" item-text="text">
                </v-select>

                <v-select v-model="weight" :items="['100', '200', '300', '400', '500', '600', '700', '800', '900']"
                  type="number" :label="$t('harmony.platform-newWeight')" outlined dense
                  class="mt-2 list-weight-input mr-2"></v-select>

                <v-text-field v-model="sizeFont" type="number" :label="$t('harmony.platform-newSize')" outlined min="5"
                  max="50" dense class="mt-2 list-weight-input"></v-text-field>
              </div>
            </v-card-text>

            <v-card-actions class="justify-end pb-4 px-4">
              <v-btn outlined color="#ff9999" class="mt-0 rounded-md py-4 px-8" small @click="removeText"
                :loading="loadingSave" :disabled="loadingSave" :aria-label="$t('harmony.remove-text')">
                <v-icon center aria-hidden="true" color="#ff9999">
                  mdi-trash-can-outline
                </v-icon>
              </v-btn>
              <v-btn outlined color="#8e84c0" class="mt-0 rounded-md py-4 px-8" small @click="saveText"
                :loading="loadingSave" :disabled="loadingSave || !validText" :aria-label="$t('harmony.save-text')">
                <v-icon center aria-hidden="true" color="#8e84c0">
                  mdi-check-bold
                </v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <EditorIconButton v-if="getPlatformName(siteSetting.platforms[`card${cardSelect}`].name)"
          icon="mdi-trash-can-outline" :aria-label="$t('harmony.remove-platform')" @click="removePlatform" />
      </div>
    </div>

    <div class="d-flex align-center mt-2" v-if="image_or_video != 3">
      <v-text-field v-model="platformSearch" outlined dense color="#9BA9BD" :rules="[rules.noUrlPlatform]"
        @input="showPlatforms = true" :placeholder="$t('harmony.search-platform')" maxlength="50" hide-details="auto"
        class="flex-grow-1" />
      <v-tooltip bottom v-if="image_or_video != 4">
        <template v-slot:activator="{ on, attrs }">
          <v-btn outlined color="#9BA9BD" class="rounded-lg ml-2" min-width="40" width="40" height="40"
            style="padding: 0;" v-bind="attrs" v-on="on"
            :aria-label="showPlatformCard ? $t('harmony.hide-platform') : $t('harmony.show-platform')"
            @click="showPlatformCard = !showPlatformCard">
            <v-icon :color="showPlatformCard ? '#9BA9BD' : '#d3d3d3'" size="22">
              {{ showPlatformCard ? 'mdi-eye' : 'mdi-eye-off' }}
            </v-icon>
          </v-btn>
        </template>
        <span>{{ showPlatformCard ? $t('harmony.hide-platform') : $t('harmony.show-platform') }}</span>
      </v-tooltip>
    </div>

    <div class="frame-div platforms-div mt-3" :class="getBgPlatforms(colorPlatform)"
      v-show="showPlatforms && image_or_video != 3 && showDetailsPlatform">
      <div v-for="platform in filteredPlatforms" :key="platform.name" class="d-flex justify-center align-center">
        <span v-if="!getPlatformImage(platform)" @click="selectPlatform(platform.name)"
          :style="{ color: colorPlatform === 2 ? 'white' : 'black' }" class="newPlatform">
          {{ platform.name }}
        </span>
        <img v-if="getPlatformImage(platform)" :src="getPlatformImage(platform)" class="platforms"
          @click="selectPlatform(platform.name)">
      </div>

      <div v-if="!hasExactMatch" class="mt-2">
        <v-btn outlined color="#8e84c0" @click="addPlatform" class="py-4 px-3 rounded-lg" small
          :disabled="!validPlatform">
          <v-icon left>mdi-plus</v-icon>
          {{ $t("harmony.add-platform") }}
        </v-btn>
      </div>
    </div>

    <EditorButtonGroup v-show="showPlatforms && image_or_video != 3 && image_or_video != 4" :options="[
      { label: $t('harmony.black'), value: 1 },
      { label: $t('harmony.white'), value: 2 },
      { label: 'Color', value: 3 }
    ]" :active="colorPlatform" @select="changeColor" class="mt-2" />

    <EditorSectionLabel
      v-show="showDetailsPlatform && showPlatforms && image_or_video != 3 && image_or_video != 4 && (siteSetting.platforms[`card${cardSelect}`].size === 'medium' || siteSetting.platforms[`card${cardSelect}`].size === 'large')"
      text="Posición" class="mt-3 mb-1" />
    <EditorButtonGroup
      v-show="showDetailsPlatform && showPlatforms && image_or_video != 3 && image_or_video != 4 && (siteSetting.platforms[`card${cardSelect}`].size === 'medium' || siteSetting.platforms[`card${cardSelect}`].size === 'large')"
      :options="[
        { label: 'Arriba', value: 'top' },
        { label: 'Centro', value: 'center' },
        { label: 'Abajo', value: 'bottom' }
      ]" :active="siteSetting.platforms[`card${cardSelect}`].logo_position" @select="changeLogoPosition" class="mt-1" />

    <div v-show="showNick && image_or_video != 3 && showDetailsPlatform" class="mt-2">
      <EditorSectionLabel class="ma-0 mb-1">
        {{ $t('harmony.your-nick', {
          platform: getPlatformName(siteSetting.platforms[`card${cardSelect}`].name)
        }) }}
      </EditorSectionLabel>
      <v-text-field v-model="siteSetting.platforms[`card${cardSelect}`].nick" color="#9BA9BD"
        :placeholder="$t('harmony.enter')" outlined dense :rules="[rules.noUrl]" hide-details="auto"></v-text-field>
    </div>

    <div v-if="selectNewPlatform != '' && showDetailsPlatform" class="mt-2">
      <EditorSectionLabel :text="$t('harmony.link-url')" />
      <v-text-field v-model="siteSetting.platforms[`card${cardSelect}`].url" color="#9BA9BD"
        :placeholder="$t('harmony.enter')" outlined dense hide-details="auto">
      </v-text-field>
    </div>

    <div v-if="showUrl2 && image_or_video != 3 && showDetailsPlatform" class="mt-2">
      <EditorSectionLabel>
        {{ getUrl2Label() }}
      </EditorSectionLabel>
      <v-text-field v-model="siteSetting.platforms[`card${cardSelect}`].url2" color="#9BA9BD"
        :placeholder="$t('harmony.enter')" outlined dense hide-details="auto">
      </v-text-field>
    </div>

    <template #actions>
      <v-btn v-if="!showEdit && !showAddSchedule" outlined class="rounded-md py-4 px-10 editor-panel__confirm-btn" small
        :disabled="!valid || loadingSave" :loading="loadingSave" @click="saveSettings" :aria-label="$t('harmony.save')">
        <v-icon center aria-hidden="true">mdi-check-bold</v-icon>
      </v-btn>

      <v-btn v-if="!showEdit && showAddSchedule" :disabled="!isFormValid" :loading="loadingSave" outlined
        class="rounded-md py-4 px-10 editor-panel__confirm-btn" small @click="saveSchedule"
        :aria-label="$t('harmony.save-schedule')">
        <v-icon center aria-hidden="true">mdi-plus</v-icon>
      </v-btn>

      <v-btn v-if="showEdit" :loading="loadingSave" outlined class="rounded-md py-4 px-10 editor-panel__confirm-btn"
        small @click="editSchedule" :aria-label="$t('harmony.edit-schedule')">
        <v-icon center aria-hidden="true">mdi-square-edit-outline</v-icon>
      </v-btn>
    </template>
  </EditorPanel>
</template>

<script>
import EditorPanel from './EditorPanel.vue'
import EditorButtonGroup from './shared/EditorButtonGroup.vue'
import EditorIconButton from './shared/EditorIconButton.vue'
import EditorSectionLabel from './shared/EditorSectionLabel.vue'

const URL2_PLATFORMS = [
  'INSTAGRAM', 'TIKTOK', 'FACEBOOK', 'X', 'YOUTUBE', 'SPOTIFY', 'VIMEO',
  'THREADS', 'LINKEDIN', 'PINTEREST', 'REDDIT', 'DISCORD', 'TELEGRAM',
  'WHATSAPP', 'AMAZON', 'MANYVIDS', 'KWAI', 'EROME', 'MYDIRTYHOBBY',
  'MYCLUB', 'LIVESTRIP', 'LOVENSE', 'PAYPAL', 'REDGITFS', 'MYWISHCAM',
  'SAKURALIVE', 'SEX.COM', 'SKYPRIVATE', 'XHAMSTER', 'KWIKY', 'REDGIFS', 'FLINBO'
];

export default {
  name: 'EditorCardPanel',
  components: {
    EditorPanel,
    EditorButtonGroup,
    EditorIconButton,
    EditorSectionLabel,
    ImageUpload: () => import('@/components/harmony/uploads/ImageUpload.vue'),
    VideoUpload: () => import('@/components/harmony/uploads/VideoUpload.vue'),
    ImageUploadGallery: () => import('@/components/harmony/uploads/ImageUploadGallery.vue'),
    ScheduleUpload: () => import('@/components/harmony/uploads/ScheduleUpload.vue')
  },
  props: {
    harmony: {
      type: Object,
      required: true
    },
    siteSetting: {
      type: Object,
      required: true
    },
    cardSelect: {
      type: Number,
      required: true
    },
    platforms: {
      type: Array,
      required: true
    },
    loadingSave: {
      type: Boolean,
      default: false
    },
    itemsForUpload: {
      type: Array,
      default: () => []
    },
    colors: {
      type: Array,
      default: () => []
    },
    fontsOptions: {
      type: Array,
      default: () => []
    },
    menuEditor: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      image_or_video: 1,
      platformSearch: '',
      colorPlatform: 1,
      showPlatforms: false,
      showNick: false,
      showUrl: false,
      showUrl2: false,
      showDetailsPlatform: true,
      selectBackground: 1,
      showEdit: false,
      showAddSchedule: false,
      isFormValid: false,
      showSchedules: false,
      dialogText: false,
      galleryUpdated: false,
      selectNewPlatform: '',
      tempText: '',
      tempColorText: '',
      fontClass: '',
      fontWeight: '',
      fontSize: '',
      colorOptions: [
        { text: 'Blanco', value: '#FFFFFF' },
        { text: 'Negro', value: '#000000' }
      ],
      rules: {
        noUrl: value => {
          if (!value) return true;
          const urlPattern = /^[a-zA-Z0-9._@-]+$/;
          return urlPattern.test(value) || this.$t('harmony.invalid-nick');
        },
        noUrlPlatform: value => {
          if (!value) return true;
          const urlPattern = /^[a-zA-Z0-9._@]+$/;
          return urlPattern.test(value) || this.$t('harmony.invalid-platform');
        },
        required: value => !!value || this.$t('navbar.required')
      }
    }
  },
  computed: {
    selectedCard() {
      return this.siteSetting.platforms[`card${this.cardSelect}`];
    },
    valid() {
      if (this.selectedCard === undefined) {
        return false;
      }
      return this.rules.noUrl(this.selectedCard.nick) === true;
    },
    validPlatform() {
      return this.rules.noUrlPlatform(this.platformSearch) === true;
    },
    validText() {
      const textToValidate = this.tempText || this.selectedCard.text;
      return this.rules.required(textToValidate) === true;
    },
    filteredPlatforms() {
      return this.platforms.filter(platform =>
        platform.name.toLowerCase().includes(this.platformSearch.toLowerCase())
      );
    },
    hasExactMatch() {
      if (!this.platformSearch) return false;
      return this.platforms.some(platform =>
        platform.name.toLowerCase() === this.platformSearch.toLowerCase()
      );
    },
    showPlatformCard: {
      get() {
        return this.siteSetting.platforms[`card${this.cardSelect}`].show;
      },
      set(value) {
        this.siteSetting.platforms[`card${this.cardSelect}`].show = value;
      }
    },
    isDisabled() {
      const cardKey = `card${this.cardSelect}`;
      const schedule = this.siteSetting.schedules?.[cardKey];
      const hasSchedule = this.hasSchedule(schedule);
      const hasNumSection = !!(schedule?.numSection);
      const hasBackground = !!(this.siteSetting.schedules?.[`card${this.cardSelect}`]?.background);

      if (this.image_or_video === 4) {
        return hasSchedule || hasNumSection || hasBackground;
      } else if (this.image_or_video === 2) {
        return !!this.siteSetting.videos?.[cardKey]?.path;
      } else if (this.image_or_video === 3) {
        return this.hasImages(this.siteSetting.galleries?.[cardKey]);
      } else if (this.image_or_video === 1) {
        return !!this.siteSetting.backgrounds?.[cardKey]?.path;
      }
      return false;
    },
    defaultImageOrVideo() {
      const hasImage = !!this.siteSetting.backgrounds[`card${this.cardSelect}`]?.path;
      const hasVideo = !!this.siteSetting.videos[`card${this.cardSelect}`]?.path;
      const hasGallery = this.hasImages(this.siteSetting.galleries[`card${this.cardSelect}`]);
      const hasSchedule = this.hasSchedule(this.siteSetting.schedules[`card${this.cardSelect}`]);
      const hasNumSection = !!(this.siteSetting.schedules[`card${this.cardSelect}`]?.numSection);
      const hasBackground = !!(this.siteSetting.schedules[`card${this.cardSelect}`]?.background);

      if (hasImage && !hasNumSection && !hasSchedule) {
        return 1;
      } else if (hasVideo) {
        return 2;
      } else if (hasGallery) {
        return 3;
      } else if (hasSchedule || hasNumSection || hasBackground) {
        return 4;
      }
      return 1;
    },
    font: {
      get() {
        return this.fontClass || this.selectedCard.fonts.font;
      },
      set(value) {
        this.fontClass = value;
      }
    },
    weight: {
      get() {
        return this.fontWeight || this.selectedCard.fonts.weight;
      },
      set(value) {
        this.fontWeight = value;
      }
    },
    sizeFont: {
      get() {
        return this.fontSize || this.selectedCard.fonts.size;
      },
      set(value) {
        this.fontSize = value;
      }
    },
    color: {
      get() {
        return this.tempColorText || this.selectedCard.textColor;
      },
      set(value) {
        this.tempColorText = value;
      }
    }
  },
  watch: {
    cardSelect() {
      this.image_or_video = this.defaultImageOrVideo;
      this.syncCardState();
    },
    image_or_video(newValue) {
      if (newValue === 3) {
        const cardKey = `card${this.cardSelect}`;
        this.siteSetting.platforms[cardKey].name = '';
        this.siteSetting.platforms[cardKey].nick = '';
        this.siteSetting.platforms[cardKey].url = '';
        this.siteSetting.platforms[cardKey].url2 = '';
        this.syncCardState();
        this.$emit('save-settings-auto');
      }
    },
    menuEditor(newValue) {
      if (!newValue) {
        this.showNick = false;
        this.showPlatforms = false;
        this.showUrl = false;
        this.platformSearch = '';
        this.selectNewPlatform = '';
        this.showUrl2 = false;
        this.$emit('save-settings-auto');
      } else {
        this.image_or_video = this.defaultImageOrVideo;
        this.syncCardState();
      }
      if (this.image_or_video == 4 && newValue) {
        if (this.$refs.UploadSchedule) {
          this.$refs.UploadSchedule.onAddScheduleClosed();
        }
      }
    },
    selectedCard: {
      handler() {
        this.syncCardState();
      },
      deep: true
    }
  },
  mounted() {
    this.image_or_video = this.defaultImageOrVideo;
    this.syncCardState();
    this.$root.$on('deleteSchedule', () => {
      this.removeLastSchedule();
    });
  },
  beforeDestroy() {
    this.$root.$off('deleteSchedule');
  },
  methods: {
    syncCardState() {
      const card = this.siteSetting?.platforms?.[`card${this.cardSelect}`];
      if (card && card.name) {
        const platformBaseName = card.name.split('/')[0];
        this.platformSearch = platformBaseName;
        this.showPlatforms = true;
        this.showNick = true;

        const selectedPlatform = this.platforms.find(
          p => p.name.toLowerCase() === platformBaseName.toLowerCase()
        );
        const hasIcons = selectedPlatform && (selectedPlatform.srcBlack || selectedPlatform.srcWhite || selectedPlatform.srcColor);

        if (hasIcons) {
          this.showUrl = false;
          this.selectNewPlatform = '';
          this.showUrl2 = this.isUrl2Supported(platformBaseName);

          const parts = card.name.split('/');
          if (parts[1]) {
            const fileName = parts[1].toUpperCase();
            if (fileName.includes('COLOR')) {
              this.colorPlatform = 3;
            } else if (fileName.includes('WHITE') || fileName.includes('BLANCO')) {
              this.colorPlatform = 2;
            } else {
              this.colorPlatform = 1;
            }
          }
        } else {
          this.selectNewPlatform = platformBaseName;
          this.showUrl = true;
          this.showUrl2 = false;
          if (card.color) {
            this.colorPlatform = card.color === 2 ? 1 : 2;
          }
        }

        if (!this.dialogText) {
          this.tempText = card.text || '';
          this.tempColorText = card.textColor || '#000000';
          this.fontClass = card.fonts?.font || '';
          this.fontWeight = card.fonts?.weight || '';
          this.fontSize = card.fonts?.size || '';
        }
      } else {
        this.platformSearch = '';
        this.selectNewPlatform = '';
        this.showPlatforms = false;
        this.showNick = false;
        this.showUrl = false;
        this.showUrl2 = false;
        this.colorPlatform = 1;
        if (!this.dialogText) {
          this.tempText = '';
          this.tempColorText = '#000000';
          this.fontClass = '';
          this.fontWeight = '';
          this.fontSize = '';
        }
      }
      this.checkPlatformSchedule();
    },
    saveSettings() {
      this.updateTextPlatforms();
      this.$emit('save-settings');
    },
    handleImageUploaded() {
      this.$emit('image-uploaded');
    },
    handleVideoUploaded() {
      this.$emit('video-uploaded');
    },
    removeBackground(value) {
      this.$emit('remove-background', value);
    },
    removeVideo(value) {
      this.$emit('remove-video', value);
    },
    removeGallery(value) {
      this.$emit('remove-gallery', value);
    },
    removeSchedules(section) {
      this.$emit('remove-schedules', section);
    },
    removeLastSchedule() {
      this.$emit('remove-last-schedule', [`card${this.cardSelect}`]);
    },
    addNewImagesGallery() {
      this.galleryUpdated = true;
      this.$refs.imageUploadGallery.triggerFileInput();
    },
    selectColor(color) {
      this.$emit('select-color', color);
    },
    showBtnSchedule(eventData) {
      this.showAddSchedule = eventData.showAddSchedule;
      this.isFormValid = eventData.enable_or_disabled;
    },
    editSchedule() {
      this.showEdit = false;
      this.showAddSchedule = false;
      this.$refs.UploadSchedule.editSchedule();
    },
    saveSchedule() {
      this.showAddSchedule = false;
      this.$refs.UploadSchedule.saveSchedule();
    },
    getPlatformImage(platform) {
      if (this.colorPlatform === 1) {
        return platform.srcBlack;
      } else if (this.colorPlatform === 2) {
        return platform.srcWhite;
      } else {
        return platform.srcColor;
      }
    },
    selectPlatform(platformName) {
      const selectedPlatform = this.platforms.find(
        platform => platform.name.toLowerCase() === platformName.toLowerCase()
      );
      this.selectNewPlatform = '';

      if (selectedPlatform) {
        this.platformSearch = selectedPlatform.name;
        this.showPlatforms = true;
        this.showNick = true;
        const cardKey = `card${this.cardSelect}`;
        let src;

        if (this.colorPlatform === 1) {
          src = selectedPlatform.srcBlack;
        } else if (this.colorPlatform === 2) {
          src = selectedPlatform.srcWhite;
        } else {
          src = selectedPlatform.srcColor;
        }

        if (src) {
          this.showUrl = false;
          const fileName = src.split('/').pop();
          this.showUrl2 = this.isUrl2Supported(selectedPlatform.name);
          this.siteSetting.platforms[cardKey].name = `${selectedPlatform.name}/${fileName}`;
        } else {
          this.selectNewPlatform = selectedPlatform.name;
          this.showUrl = true;
          this.showUrl2 = false;
          this.siteSetting.platforms[cardKey].name = selectedPlatform.name;
          this.siteSetting.platforms[cardKey].color = this.colorPlatform === 1 ? 2 : 1;
        }
      }
    },
    changeColor(color) {
      this.colorPlatform = color;
      const cardKey = `card${this.cardSelect}`;
      const platformName = this.siteSetting.platforms[cardKey].name.split('/')[0];
      const selectPlatform = this.platforms.find(
        platform => platform.name.toLowerCase() === platformName.toLowerCase()
      );

      if (selectPlatform) {
        if (selectPlatform.srcBlack || selectPlatform.srcWhite || selectPlatform.srcColor) {
          let src;
          if (this.colorPlatform === 1) {
            src = selectPlatform.srcBlack;
          } else if (this.colorPlatform === 2) {
            src = selectPlatform.srcWhite;
          } else {
            src = selectPlatform.srcColor;
          }
          if (src) {
            const parts = src.split('/');
            const fileName = parts[parts.length - 1];
            this.siteSetting.platforms[cardKey].name = `${selectPlatform.name}/${fileName}`;
          }
        } else {
          if (this.colorPlatform === 1) {
            this.siteSetting.platforms[cardKey].color = 2;
          } else {
            this.siteSetting.platforms[cardKey].color = 1;
          }
        }
      }
    },
    changeLogoPosition(position) {
      this.siteSetting.platforms[`card${this.cardSelect}`].logo_position = position;
    },
    removePlatform() {
      const cardKey = `card${this.cardSelect}`;
      this.siteSetting.platforms[cardKey].name = '';
      this.siteSetting.platforms[cardKey].nick = '';
      this.siteSetting.platforms[cardKey].url = '';
      this.siteSetting.platforms[cardKey].url2 = '';
      this.siteSetting.platforms[cardKey].text = '';
      this.siteSetting.platforms[cardKey].textColor = '#000000';
      if (this.siteSetting.platforms[cardKey].fonts) {
        this.siteSetting.platforms[cardKey].fonts.font = '';
        this.siteSetting.platforms[cardKey].fonts.weight = '';
        this.siteSetting.platforms[cardKey].fonts.size = '';
      }
      this.tempText = '';
      this.tempColorText = '#000000';
      this.fontClass = '';
      this.fontWeight = '';
      this.fontSize = '';
      this.platformSearch = '';
      this.selectNewPlatform = '';
      this.showPlatforms = false;
      this.showNick = false;
      this.showUrl = false;
      this.showUrl2 = false;
      this.colorPlatform = 1;
      this.$emit('save-settings');
    },
    removeText() {
      this.tempText = '';
      this.tempColorText = '#000000';
      this.fontClass = 'font-rubik';
      this.fontSize = '20';
      this.fontWeight = '700';
      this.updateTextPlatforms();
      this.$emit('save-settings');
      this.$refs.platformText.resetValidation();
      this.dialogText = false;
    },
    saveText() {
      this.updateTextPlatforms();
      this.$emit('save-settings');
      this.dialogText = false;
    },
    updateTextPlatforms() {
      const card = this.siteSetting.platforms[`card${this.cardSelect}`];
      if (card) {
        if (this.fontSize > 50) {
          this.fontSize = 50;
        }
        if (this.fontSize < 5) {
          this.fontSize = 5;
        }
        card.text = this.tempText;
        card.textColor = this.tempColorText;
        card.fonts.font = this.fontClass;
        card.fonts.weight = this.fontWeight;
        card.fonts.size = this.fontSize;
      }
    },
    addPlatform() {
      const newPlatform = {
        name: this.platformSearch,
        color: this.colorPlatform
      };
      const existingPlatform = this.platforms.find(
        platform => platform.name.toLowerCase() === newPlatform.name.toLowerCase()
      );
      if (!existingPlatform) {
        this.platforms.push(newPlatform);
        this.$emit('add-platform', newPlatform);
        this.selectPlatform(newPlatform.name);
      } else {
        this.selectPlatform(existingPlatform.name);
      }
    },
    getPlatformName(fullPath) {
      if (fullPath) {
        const parts = fullPath.split('/');
        return parts[0];
      }
      return '';
    },
    getUrl2Label() {
      const card = this.siteSetting?.platforms?.[`card${this.cardSelect}`];
      if (!card || !card.name) return this.$t('harmony.optional-post-link');
      const platformName = card.name.split('/')[0].toUpperCase();
      if (platformName === 'YOUTUBE') {
        return this.$t('harmony.optional-video-link');
      } else if (platformName === 'SPOTIFY') {
        return this.$t('harmony.optional-spotify-link');
      } else {
        return this.$t('harmony.optional-post-link');
      }
    },
    isUrl2Supported(platformName) {
      if (!platformName) return false;
      const name = platformName.split('/')[0].toUpperCase();
      return URL2_PLATFORMS.includes(name);
    },
    getBgPlatforms(value) {
      if (value == 1) {
        return '';
      } else if (value == 2) {
        return 'bg-platform';
      } else {
        return 'bg-platform2';
      }
    },
    hasImages(gallery) {
      return gallery && gallery.images && gallery.images.length > 0;
    },
    hasSchedule(card) {
      return card && card.schedule && card.schedule.length > 0;
    },
    checkPlatformSchedule() {
      if (this.image_or_video === 4 && this.siteSetting.platforms[`card${this.cardSelect}`].name) {
        this.showDetailsPlatform = true;
      } else {
        this.showDetailsPlatform = true;
      }
    },
    formatText(item) {
      return `${item.fontName} : ${item.text}`;
    }
  }
}
</script>

<style scoped>
@import './editor-panel.css';

.platforms-div {
  min-height: 104px;
  max-height: 120px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--editor-space-2);
}

.newPlatform {
  font-size: 24px;
  font-weight: bold;
  text-align: center;
  cursor: pointer;
}

.platforms {
  max-width: 165px;
  max-height: 60px;
  object-fit: contain;
  cursor: pointer;
  transition: transform 0.2s ease-in-out;
}

.platforms:hover {
  transform: scale(1.05);
}

.bg-platform {
  background-color: #6b6b6b;
}

.bg-platform2 {
  background-color: #dfdbdb;
}

.close-button {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1;
}

.list-fonts {
  max-width: 280px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.list-fonts-input {
  max-width: 320.28px;
}

.list-weight-input {
  width: 90px !important;
}
</style>
